/** Fill synthetic advisor screener rows from StockForge snapshot + Market Data quotes. */

import {
  fetchMarketdataOptionQuotesBatch,
  getCachedMarketdataOptionQuote,
  type MarketdataOptionQuote,
} from "../api/marketdataOptionQuotes";
import {
  buildRecommendationLookup,
  loadTickerReport,
  normalizeTicker,
  peekRecommendationsSnapshot,
  recommendationLookupKey,
  type PositionsSnapshot,
  type TickerReport,
  type WheelRecommendation,
} from "./watchlistData";

function num(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function takeNum<K extends keyof WheelRecommendation>(
  target: WheelRecommendation,
  source: Partial<WheelRecommendation>,
  key: K,
): void {
  if (target[key] != null && Number.isFinite(Number(target[key]))) return;
  const v = num(source[key]);
  if (v != null) (target as Record<string, unknown>)[key as string] = v;
}

function takeStr<K extends keyof WheelRecommendation>(
  target: WheelRecommendation,
  source: Partial<WheelRecommendation>,
  key: K,
): void {
  if (String(target[key] ?? "").trim()) return;
  const v = source[key];
  if (typeof v === "string" && v.trim()) (target as Record<string, unknown>)[key as string] = v;
}

function mergeContractFields(target: WheelRecommendation, source: WheelRecommendation): void {
  takeNum(target, source, "mid");
  takeNum(target, source, "last_price");
  takeNum(target, source, "bid");
  takeNum(target, source, "ask");
  takeNum(target, source, "pop");
  takeNum(target, source, "roi");
  takeNum(target, source, "dte");
  takeNum(target, source, "underlying_price");
  takeNum(target, source, "target_price");
  takeNum(target, source, "market_cap");
  takeNum(target, source, "collateral");
  takeStr(target, source, "sector");
  takeStr(target, source, "decision");
  takeStr(target, source, "report_state");
  takeStr(target, source, "next_earnings_date");
  if (target.expires_after_earnings == null && source.expires_after_earnings != null) {
    target.expires_after_earnings = source.expires_after_earnings;
  }
  if (!target.option_symbol && source.option_symbol) target.option_symbol = source.option_symbol;
  if (!target.index_badges?.length && source.index_badges?.length) {
    target.index_badges = source.index_badges;
  }
}

function mergeTickerFields(target: WheelRecommendation, source: WheelRecommendation): void {
  takeNum(target, source, "underlying_price");
  takeNum(target, source, "target_price");
  takeNum(target, source, "market_cap");
  takeStr(target, source, "sector");
  takeStr(target, source, "decision");
  takeStr(target, source, "report_state");
  takeStr(target, source, "next_earnings_date");
  if (target.expires_after_earnings == null && source.expires_after_earnings != null) {
    target.expires_after_earnings = source.expires_after_earnings;
  }
  if (!target.index_badges?.length && source.index_badges?.length) {
    target.index_badges = source.index_badges;
  }
}

function applyQuote(target: WheelRecommendation, quote: MarketdataOptionQuote): void {
  if (target.mid == null && quote.mid != null) target.mid = quote.mid;
  if (target.last_price == null && quote.last != null) target.last_price = quote.last;
  if (target.bid == null && quote.bid != null) target.bid = quote.bid;
  if (target.ask == null && quote.ask != null) target.ask = quote.ask;
  if (target.pop == null && quote.pop != null) target.pop = quote.pop;
}

function applyReport(target: WheelRecommendation, report: TickerReport | null): void {
  if (!report) return;
  if (target.underlying_price == null && report.price != null) target.underlying_price = report.price;
  if (target.target_price == null && report.target_price != null) target.target_price = report.target_price;
  if (!target.sector && report.sector) target.sector = report.sector;
  if (!target.decision && report.decision) {
    target.decision = report.decision;
    target.report_state = "available";
  }
  if (target.market_cap == null && report.market_cap != null) target.market_cap = report.market_cap;
}

function annualizedRoi(premium: number | undefined, basis: number | undefined, dte: number | undefined): number | undefined {
  if (premium == null || !(premium > 0) || basis == null || !(basis > 0) || dte == null || !(dte > 0)) {
    return undefined;
  }
  return (premium / basis) * (365 / dte);
}

function applyDerived(target: WheelRecommendation): void {
  const mark = target.mid ?? target.last_price;
  const basis = target.holding_avg_cost ?? target.strike;
  if (target.roi == null) {
    const roi = annualizedRoi(mark, basis, target.dte);
    if (roi != null) target.roi = roi;
  }
  if (target.collateral == null && target.strike > 0) {
    target.collateral = target.strike * 100;
  }
  if (target.next_earnings_date && target.expiration_date && target.expires_after_earnings == null) {
    target.expires_after_earnings = target.next_earnings_date <= target.expiration_date;
  }
}

function occKey(symbol: string | undefined): string {
  return String(symbol || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

export function enrichAdviceScreenerRow(
  row: WheelRecommendation,
  allRows: WheelRecommendation[],
  positions?: PositionsSnapshot | null,
): WheelRecommendation {
  const out: WheelRecommendation = { ...row };
  const snapshot = peekRecommendationsSnapshot();
  const lookup = snapshot ? buildRecommendationLookup(snapshot) : null;
  const occ = occKey(out.option_symbol);
  const ticker = normalizeTicker(out.underlying_ticker);

  const exact =
    (occ && lookup?.get(`sym:${occ}`)) ||
    (ticker && out.strike > 0 && out.expiration_date
      ? lookup?.get(recommendationLookupKey(ticker, out.strike, out.expiration_date))
      : undefined);
  if (exact) mergeContractFields(out, exact);

  const peer = allRows.find((r) => normalizeTicker(r.underlying_ticker) === ticker);
  if (peer) mergeTickerFields(out, peer);

  const stock = positions?.stock.find((p) => normalizeTicker(p.ticker) === ticker);
  if (stock) {
    if (out.underlying_price == null && stock.current_price != null) out.underlying_price = stock.current_price;
    if (out.target_price == null && stock.target_price != null) out.target_price = stock.target_price;
    if (!out.sector && stock.sector) out.sector = stock.sector;
  }

  if (occ) {
    const cached = getCachedMarketdataOptionQuote(occ);
    if (cached) applyQuote(out, cached);
  }
  applyReport(out, reportCache.get(ticker) ?? null);

  applyDerived(out);
  return out;
}

function needsQuote(row: WheelRecommendation): boolean {
  const occ = occKey(row.option_symbol);
  if (!occ) return false;
  if (getCachedMarketdataOptionQuote(occ) !== undefined) return false;
  return row.mid == null && row.last_price == null;
}

const reportCache = new Map<string, TickerReport | null>();
const reportInflight = new Map<string, Promise<TickerReport | null>>();

async function reportForTicker(ticker: string): Promise<TickerReport | null> {
  const key = normalizeTicker(ticker);
  if (!key) return null;
  const existing = reportInflight.get(key);
  if (existing) return existing;
  const pending = loadTickerReport(key)
    .then((report) => {
      reportCache.set(key, report);
      return report;
    })
    .finally(() => reportInflight.delete(key));
  reportInflight.set(key, pending);
  return pending;
}

/** Fetch missing Market Data quotes + SF reports. Returns true when a rerender should pick up new fields. */
export async function hydrateAdviceScreenerRows(rows: WheelRecommendation[]): Promise<boolean> {
  const synthetic = rows.filter((r) => r.sfe_advice);
  if (!synthetic.length) return false;

  const quoteSymbols = synthetic.filter(needsQuote).map((r) => occKey(r.option_symbol)).filter(Boolean);
  const reportTickers = [
    ...new Set(
      synthetic
        .filter((r) => !r.decision || r.underlying_price == null || !r.sector)
        .map((r) => normalizeTicker(r.underlying_ticker))
        .filter((t) => t && !reportCache.has(t)),
    ),
  ];

  let changed = false;
  if (quoteSymbols.length) {
    const result = await fetchMarketdataOptionQuotesBatch(quoteSymbols);
    if (Object.keys(result.quotes).length) changed = true;
  }
  if (reportTickers.length) {
    await Promise.all(reportTickers.map((t) => reportForTicker(t)));
    changed = true;
  }
  return changed;
}
